<template>
  <div class="thi">
    <div class="thi-topbar">
      <div class="thi-title">
        <span class="thi-n">{{ allPosts.length }}</span>
        <span class="thi-lbl">{{ $t('happilyVillage.Town_Hall_Topics') }}</span>
      </div>
      <div class="thi-topbar-right">
        <div class="thi-sections">
          <span class="thi-sec" :class="{ on: section === 'all' }" @click="setSection('all')">All</span>
          <span class="thi-sec" :class="{ on: section === 'pinned' }" @click="setSection('pinned')">Pinned <i v-if="counts.pinned" class="thi-cnt">{{ counts.pinned }}</i></span>
          <span class="thi-sec" :class="{ on: section === 'scheduled' }" @click="setSection('scheduled')">Scheduled <i v-if="counts.scheduled" class="thi-cnt">{{ counts.scheduled }}</i></span>
          <span class="thi-sec" :class="{ on: section === 'drafts' }" @click="setSection('drafts')">Drafts <i v-if="counts.drafts" class="thi-cnt">{{ counts.drafts }}</i></span>
        </div>
        <div class="thi-composewrap">
          <button type="button" class="thi-compose" @click="showComposeMenu = !showComposeMenu">
            <span class="thi-compose-plus">+</span> Compose <i class="el-icon-arrow-down" />
          </button>
          <div class="thi-composemenu" v-if="showComposeMenu">
            <div class="thi-composemenu-item" @click="compose('Topic')"><i class="el-icon-chat-line-square" /> New topic</div>
            <div class="thi-composemenu-item" @click="compose('Poll')"><i class="el-icon-data-line" /> New poll</div>
            <div class="thi-composemenu-item" @click="compose('Checklist')"><i class="el-icon-finished" /> New checklist</div>
            <div class="thi-composemenu-divider" />
            <div v-if="reelsEnabled" class="thi-composemenu-item" @click="compose('Reel')"><i class="el-icon-video-camera" /> {{ $t('happilyVillage.reels.new_reel') }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="thi-grid">
      <div class="thi-list-col">
        <div class="thi-toolbar">
          <input class="thi-search" v-model="query" :placeholder="'Search or #tag…'" />
          <div class="thi-tagwrap">
            <span class="thi-tagbtn" :class="{ on: activeTag }" @click="showTagMenu = !showTagMenu">#<i class="el-icon-arrow-down" /></span>
            <div class="thi-tagmenu" v-if="showTagMenu">
              <div class="thi-tagmenu-head">Topics · most used</div>
              <div v-if="!trending.length" class="thi-tagmenu-empty">No tags yet</div>
              <div class="thi-tagmenu-item" v-for="t in trending" :key="t.name" :class="{ sel: activeTag === t.name }" @click="toggleTag(t.name)">
                <span class="t">#{{ t.name }}</span><span class="c">{{ t.count }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="thi-activefilter" v-if="activeTag">
          <span class="thi-chip">#{{ activeTag }} <i class="el-icon-close" @click="activeTag = ''" /></span>
        </div>

        <div class="thi-scroll" ref="scroll" @scroll="onScroll">
          <div v-if="!groups.length" class="thi-empty">No posts here yet.</div>
          <div v-for="grp in visibleGroups" :key="grp.label">
            <div class="thi-grouphead">{{ grp.label }}</div>
            <div
              class="thi-msg"
              v-for="p in grp.items"
              :key="p.key || p.id"
              :class="{ sel: selectedPost && (selectedPost.key === p.key) }"
              @click="select(p)"
            >
              <span class="thi-type" :style="{ color: typeMeta(p).color, background: tint(typeMeta(p).color) }"><i :class="typeMeta(p).icon"></i></span>
              <div class="thi-msg-col">
                <div class="thi-msg-l1">
                  <i v-if="isOfficial(p)" class="el-icon-bell thi-mega" />
                  <span class="thi-subj">{{ p.title }}</span>
                </div>
                <div class="thi-prev">{{ preview(p) }}</div>
                <div class="thi-msg-l3">
                  <template v-if="p.create_name">
                    <span class="thi-creator">{{ p.create_name }}</span>
                    <span class="thi-dot">·</span>
                  </template>
                  <span class="thi-aud">{{ audience(p) }}</span>
                  <span v-if="statusOf(p) === 'scheduled'" class="thi-mini sched"><i class="el-icon-time" /> Scheduled</span>
                  <span v-else-if="statusOf(p) === 'draft'" class="thi-mini draft">Draft</span>
                  <span v-else class="thi-read"><i class="el-icon-view" /> {{ p.read || 0 }}</span>
                  <span class="thi-time">{{ isScheduled(p) ? scheduledWhen(p) : timeOf(p) }}</span>
                </div>
              </div>
            </div>
          </div>
          <div v-if="hasMore" class="thi-more" @click="showMore">Show more ({{ flatVisible.length - shownCount }})</div>
        </div>
      </div>

      <div class="thi-reader" v-if="selectedPost">
        <div class="thi-rd-head">
          <div class="thi-rd-eyebrow">
            <span v-if="isOfficial(selectedPost)" class="thi-badge"><i class="el-icon-bell" /> Announcement</span>
            <span class="thi-rd-date">{{ typeLabel(selectedPost) }}<template v-if="isScheduled(selectedPost)"> · Scheduled for {{ scheduledWhen(selectedPost) }}</template><template v-else-if="selectedPost.createdate"> · {{ selectedPost.createdate }}</template></span>
            <div class="thi-rd-actions" v-if="canManage(selectedPost)">
              <button
                v-if="canPin(selectedPost)"
                type="button"
                role="switch"
                :aria-checked="pinMeta.pinned ? 'true' : 'false'"
                class="thi-pin-toggle"
                :class="{ on: pinMeta.pinned }"
                :disabled="pinBusy"
                :title="pinMeta.pinned ? 'Pinned to the top of Town Hall' : 'Pin to the top of Town Hall'"
                @click="togglePin"
              >
                <i :class="pinMeta.icon" />
                <span class="thi-pin-text">{{ pinMeta.pinned ? 'Pinned' : 'Pin to top' }}</span>
                <span class="thi-pin-switch"><span class="thi-pin-knob" /></span>
              </button>
              <button
                v-if="isDraft(selectedPost)"
                type="button"
                class="thi-republish"
                @click="republishPost"
              >
                <i class="el-icon-upload2" /> Publish
              </button>
              <div class="thi-actions-wrap">
                <button type="button" class="thi-actions-trigger" :title="'Post actions'" @click="showActionsMenu = !showActionsMenu">
                  <i class="el-icon-more" />
                </button>
                <div class="thi-actions-menu" v-if="showActionsMenu">
                  <div class="thi-actions-item" @click="onPostAction('edit')"><i class="el-icon-edit" /> Edit</div>
                  <div v-if="!isDraft(selectedPost)" class="thi-actions-item" @click="onPostAction('draft')"><i class="el-icon-bottom" /> Move to draft</div>
                  <div class="thi-actions-item danger" @click="onPostAction('delete')"><i class="el-icon-delete" /> Delete</div>
                </div>
              </div>
            </div>
          </div>
          <div class="thi-rd-title">{{ selectedPost.title }}</div>
          <div class="thi-rd-meta">
            <span class="thi-av sm">{{ initials(selectedPost.create_name) }}</span>
            <span>By <b>{{ selectedPost.create_name }}</b> · to <b>{{ audience(selectedPost) }}</b></span>
          </div>
          <div class="thi-rd-tags" v-if="tagsOf(selectedPost).length">
            <span class="thi-chip clickable" v-for="t in tagsOf(selectedPost)" :key="t" @click="toggleTag(t)">#{{ t }}</span>
          </div>
        </div>
        <div v-if="isScheduled(selectedPost)" class="thi-sched-banner">
          <span class="thi-sched-when"><i class="el-icon-time" /> Scheduled for {{ scheduledWhen(selectedPost) }}</span>
          <button type="button" class="thi-sched-cancel" @click="cancelSchedule(selectedPost)">Cancel schedule</button>
        </div>
        <div v-if="isDraft(selectedPost)" class="thi-sched-banner thi-draft-banner">
          <span class="thi-sched-when"><i class="el-icon-document" /> Draft — not visible in the employee app</span>
        </div>
        <div class="thi-reach" v-if="isLive(selectedPost)">
          <span class="thi-reach-pill"><i class="el-icon-view" /> Read {{ selectedPost.read || 0 }}</span>
          <span class="thi-reach-meta">{{ commentCount(selectedPost) }} comments</span>
        </div>
        <div class="thi-rd-scroll">
          <div class="thi-rd-body">
            <MarkdownView v-if="selectedPost.description_md" :source="selectedPost.description_md" />
            <div v-else class="thi-body-text">{{ selectedPost.description }}</div>
          </div>

          <div v-if="selectedPost.type === 'poll'" class="thi-poll">
            <div class="thi-poll-total">{{ poll.totalVotes }} {{ poll.totalVotes === 1 ? 'vote' : 'votes' }}</div>
            <div class="thi-poll-row" v-for="(o, i) in poll.options" :key="o.label + i">
              <div class="thi-poll-label">{{ o.label }}</div>
              <div class="thi-poll-bar">
                <div class="thi-poll-track">
                  <div class="thi-poll-fill" :style="{ width: o.width + '%', backgroundColor: o.color }"></div>
                </div>
                <div class="thi-poll-percent">{{ o.pct }}%</div>
              </div>
            </div>
          </div>
          <div v-else-if="selectedPost.type === 'checklist'" class="thi-checklist">
            <div class="thi-checklist-item" v-for="(item, i) in checklist" :key="item.label + i">
              <i class="el-icon-circle-check thi-checklist-mark" />
              <span class="thi-checklist-label">{{ item.label }}</span>
            </div>
          </div>

          <div class="thi-comments" v-if="isLive(selectedPost)">
            <div class="thi-cmt-head">{{ commentCount(selectedPost) }} comments</div>
            <div v-if="commentsDisabled" class="thi-cmt-empty">Comments are turned off for this post.</div>
            <div v-else-if="!comments.length" class="thi-cmt-empty">No comments yet.</div>
            <div v-else>
              <div class="thi-cmt" v-for="c in comments" :key="c.key">
                <span class="thi-av sm">{{ initials(c.name) }}</span>
                <div class="thi-cmt-col">
                  <div class="thi-cmt-meta"><b>{{ c.name }}</b><span class="thi-cmt-time">{{ stamp(c.createdate) }}</span><span v-if="c.like" class="thi-cmt-like"><i class="el-icon-star-on" /> {{ c.like }}</span><span v-if="!commentsDisabled" class="thi-cmt-reply" @click="startReply(c)">Reply</span></div>
                  <div class="thi-cmt-text">{{ c.value }}</div>
                  <div class="thi-cmt-sub" v-for="s in c.subComment" :key="s.key">
                    <span class="thi-av xs">{{ initials(s.name) }}</span>
                    <div class="thi-cmt-col">
                      <div class="thi-cmt-meta"><b>{{ s.name }}</b><span class="thi-cmt-time">{{ stamp(s.createdate) }}</span><span v-if="!commentsDisabled" class="thi-cmt-reply" @click="startReply(c)">Reply</span></div>
                      <div class="thi-cmt-text">{{ s.value }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="thi-composer" v-if="isLive(selectedPost)">
          <div v-if="commentsDisabled" class="thi-composer-off">Comments are turned off</div>
          <template v-else>
            <div v-if="replyTarget" class="thi-replychip">
              Replying to <b>{{ replyTarget.name }}</b>
              <i class="el-icon-close" @click="cancelReply" />
            </div>
            <div class="thi-composer-row">
              <input
                class="thi-composer-input"
                v-model="draft"
                :placeholder="replyTarget ? 'Write a reply…' : 'Write a comment…'"
                @keyup.enter="submitComment"
              />
              <button class="thi-composer-send" :disabled="!canSubmit" @click="submitComment">Send</button>
            </div>
          </template>
        </div>
      </div>
      <div class="thi-reader thi-reader-empty" v-else>
        <span>Select a post to read it.</span>
      </div>
    </div>
  </div>
</template>

<script>
import { buildSectionGroups, tabCounts, topTags, postStatus, windowGroups, countGroupItems, rowTime, formatStamp, pollView, checklistView, postTypeMeta, canSubmitComment, isPinned, pinControl } from './inbox-logic'
import MarkdownView from '../../markdown/MarkdownView.vue'
import { stripMarkdown } from '../../markdown'
import moment from 'moment'

const NS = 'happilyvillageTownhall'
const PAGE = 25

export default {
  name: 'TownHallInbox',
  props: {
    reelsEnabled: { type: Boolean, default: false },
  },
  components: { MarkdownView },
  data() {
    return { section: 'all', activeTag: '', query: '', selectedKey: null, showTagMenu: false, showComposeMenu: false, showActionsMenu: false, limit: PAGE, draft: '', replyTarget: null, pinBusy: false, pinOverride: null }
  },
  computed: {
    allPosts() {
      return this.$store.getters[`${NS}/townhall_all`] || []
    },
    scheduledPosts() {
      return this.$store.getters[`${NS}/scheduledTownhall`] || []
    },
    draftPosts() {
      return this.$store.getters[`${NS}/draftTownhall`] || []
    },
    groupablePosts() {
      // Scheduled posts (townhall_scheduled) and drafts (townhall with
      // delete:'draft') live in separate getters, not townhall_all. Merge them
      // in for grouping, counts and selection.
      return this.allPosts.concat(this.scheduledPosts).concat(this.draftPosts)
    },
    townhallTags() {
      return this.$store.getters[`${NS}/townhallTags`] || { tags: {} }
    },
    todayStr() {
      const d = new Date()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${d.getFullYear()}-${m}-${day}`
    },
    counts() {
      return tabCounts(this.groupablePosts)
    },
    trending() {
      return topTags(this.townhallTags, 12)
    },
    groups() {
      return buildSectionGroups(this.groupablePosts, {
        section: this.section,
        tag: this.activeTag,
        query: this.query,
        todayStr: this.todayStr,
      })
    },
    flatVisible() {
      return this.groups.reduce((acc, g) => acc.concat(g.items), [])
    },
    visibleGroups() {
      return windowGroups(this.groups, this.limit)
    },
    shownCount() {
      return countGroupItems(this.visibleGroups)
    },
    hasMore() {
      return this.shownCount < this.flatVisible.length
    },
    selectedPost() {
      if (!this.flatVisible.length) return null
      const found = this.flatVisible.find(p => p.key === this.selectedKey)
      return found || this.flatVisible[0]
    },
    selectedKeyResolved() {
      return this.selectedPost ? this.selectedPost.key : null
    },
    comments() {
      return this.$store.getters[`${NS}/comment`] || []
    },
    commentsDisabled() {
      return !!(this.selectedPost && this.selectedPost.comment_disable)
    },
    canSubmit() {
      return canSubmitComment(this.draft)
    },
    poll() {
      return this.selectedPost ? pollView(this.selectedPost) : { totalVotes: 0, options: [] }
    },
    checklist() {
      return this.selectedPost ? checklistView(this.selectedPost) : []
    },
    pinMeta() {
      const p = this.selectedPost
      // Reflect an in-flight optimistic toggle so the switch moves the
      // instant it's clicked, before the store refetch lands.
      if (p && this.pinOverride && this.pinOverride.key === p.key) {
        return pinControl({ ...p, pined: this.pinOverride.pinned })
      }
      return pinControl(p)
    },
  },
  watch: {
    section() { this.resetWindow() },
    activeTag() { this.resetWindow() },
    query() { this.resetWindow() },
    selectedKeyResolved() {
      this.draft = ''
      this.replyTarget = null
      this.pinOverride = null
      this.showActionsMenu = false
      this.loadComments(this.selectedPost)
    },
  },
  mounted() {
    if (!this.allPosts.length) this.$store.dispatch(`${NS}/getTownhall`)
    this.$store.dispatch(`${NS}/getTownhallTags`)
    this.$store.dispatch(`${NS}/getScheduledTownhall`)
    this.$store.dispatch(`${NS}/getDraftTownhall`)
    this.loadComments(this.selectedPost)
  },
  methods: {
    resetWindow() {
      this.limit = PAGE
      const el = this.$refs.scroll
      if (el) el.scrollTop = 0
    },
    onScroll(e) {
      if (!this.hasMore) return
      const el = e.target
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 160) this.showMore()
    },
    showMore() {
      if (this.hasMore) this.limit += PAGE
    },
    select(p) {
      this.selectedKey = p.key
    },
    setSection(s) {
      this.section = s
      this.selectedKey = null
    },
    toggleTag(name) {
      this.activeTag = this.activeTag === name ? '' : name
      this.showTagMenu = false
      this.selectedKey = null
    },
    compose(type) {
      // Reuse the parent's existing create dialogs (topic/poll/checklist)
      // instead of rebuilding the proven authoring flow in the inbox.
      this.$emit('compose', type)
      this.showComposeMenu = false
    },
    initials(name) {
      return String(name || '?').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase()
    },
    typeMeta(p) {
      return postTypeMeta(p)
    },
    tint(hex) {
      // Translucent (~10%) version of the type color for the chip background.
      return typeof hex === 'string' ? hex + '1a' : 'transparent'
    },
    preview(p) {
      const s = stripMarkdown((p && (p.description_md || p.description)) || '') || ''
      if (s) return s.length > 90 ? s.slice(0, 90) + '…' : s
      const t = (p && p.type) || ''
      const n = (p && Array.isArray(p.options) && p.options.length) || 0
      if (t === 'poll') return n ? `Poll · ${n} options` : 'Poll'
      if (t === 'checklist') return n ? `Checklist · ${n} items` : 'Checklist'
      return ''
    },
    audience(p) {
      if (!p.huddles || !p.huddles.length) return 'Everyone'
      const first = (p.huddles[0] && (p.huddles[0].value || p.huddles[0].key)) || 'Team'
      return p.huddles.length > 1 ? `${first} +${p.huddles.length - 1}` : first
    },
    isOfficial(p) {
      return !!(p && p.official === true)
    },
    statusOf(p) {
      return postStatus(p)
    },
    isScheduled(p) {
      return this.statusOf(p) === 'scheduled'
    },
    scheduledWhen(p) {
      return formatStamp(p && p.publish_at_str)
    },
    cancelSchedule(p) {
      if (!p) return
      Promise.resolve(this.$store.dispatch(`${NS}/cancelScheduledPost`, { id: p.key }))
        .then(() => { if (this.$message) this.$message({ type: 'success', message: 'Schedule canceled.' }) })
        .catch(() => { if (this.$message) this.$message({ type: 'error', message: 'Could not cancel the schedule. Please try again.' }) })
    },
    canPin(p) {
      // Only live (published) posts carry a meaningful `pined` flag. Scheduled
      // posts live in townhall_scheduled and drafts aren't live yet.
      return !!p && !this.isScheduled(p) && this.statusOf(p) === 'published'
    },
    isLive(p) {
      return this.statusOf(p) === 'published'
    },
    isDraft(p) {
      return this.statusOf(p) === 'draft'
    },
    canManage(p) {
      // Published and draft posts can be edited / deleted / (un)published from
      // the reader. Scheduled posts keep their dedicated Cancel-schedule banner.
      const st = this.statusOf(p)
      return !!p && (st === 'published' || st === 'draft')
    },
    onPostAction(command) {
      this.showActionsMenu = false
      if (command === 'edit') this.editPost()
      else if (command === 'draft') this.moveToDraft()
      else if (command === 'delete') this.deletePost()
    },
    editPost() {
      // Edit reuses the parent's compose dialogs (cloneTopic), so delegate up.
      if (this.selectedPost) this.$emit('edit', this.selectedPost)
    },
    moveToDraft() {
      const p = this.selectedPost
      if (!p) return
      Promise.resolve(this.$store.dispatch(`${NS}/moveToDraft`, { id: p.key }))
        .then(() => { if (this.$message) this.$message({ type: 'success', message: 'Moved to drafts. It is no longer visible in the app.' }) })
        .catch(() => { if (this.$message) this.$message({ type: 'error', message: 'Could not move to drafts. Please try again.' }) })
    },
    republishPost() {
      const p = this.selectedPost
      if (!p) return
      Promise.resolve(this.$store.dispatch(`${NS}/republishPost`, { id: p.key }))
        .then(() => { if (this.$message) this.$message({ type: 'success', message: 'Published. It is live in the app again.' }) })
        .catch(() => { if (this.$message) this.$message({ type: 'error', message: 'Could not publish. Please try again.' }) })
    },
    deletePost() {
      const p = this.selectedPost
      if (!p) return
      const ask = this.$confirm
        ? this.$confirm(`Delete "${p.title}"? This cannot be undone.`, 'Delete post', { confirmButtonText: 'Delete', cancelButtonText: 'Cancel', type: 'warning' })
        : Promise.resolve()
      Promise.resolve(ask).then(() => {
        return Promise.resolve(this.$store.dispatch(`${NS}/deleteTopic`, p)).then(() => {
          // A deleted draft also leaves the drafts query — refresh it too.
          this.$store.dispatch(`${NS}/getDraftTownhall`)
          if (this.$message) this.$message({ type: 'success', message: 'Post deleted.' })
        })
      }).catch(() => {})
    },
    togglePin() {
      const p = this.selectedPost
      if (!p || this.pinBusy || !this.canPin(p)) return
      const next = !isPinned(p)
      this.pinBusy = true
      this.pinOverride = { key: p.key, pinned: next } // optimistic: flip the switch now
      Promise.resolve(this.$store.dispatch(`${NS}/setPinnedPost`, { id: p.key, pined: next }))
        .then(() => { if (this.$message) this.$message({ type: 'success', message: next ? 'Pinned to the top of Town Hall.' : 'Unpinned.' }) })
        .catch(() => { this.pinOverride = null; if (this.$message) this.$message({ type: 'error', message: 'Could not update the pin. Please try again.' }) })
        .then(() => { this.pinBusy = false })
    },
    typeLabel(p) {
      return postTypeMeta(p).label
    },
    commentCount(p) {
      return (p && (p.new_comments || p.commentsall || p.comments)) || 0
    },
    tagsOf(p) {
      return p && Array.isArray(p.tags) ? p.tags : []
    },
    timeOf(p) {
      return rowTime(p, this.todayStr)
    },
    stamp(s) {
      return formatStamp(s)
    },
    loadComments(post) {
      // The `comment` getter is shared module state, so clear it first to
      // avoid showing the previous post's thread under the new selection.
      this.$store.commit(`${NS}/setComment`, [])
      if (!post || post.comment_disable) return
      if (!this.commentCount(post)) return
      this.$store.dispatch(`${NS}/getComment`, { id: post.key })
    },
    startReply(c) {
      // Replies always attach to the top-level parent comment `c`, even when
      // invoked from a sub-comment's Reply affordance.
      this.replyTarget = c
    },
    cancelReply() {
      this.replyTarget = null
    },
    submitComment() {
      if (!this.canSubmit || !this.selectedPost) return
      const user = this.$store.getters['authen/user'] || {}
      // Matches the app's submitMessage/submitReply ordering key exactly.
      const likeorder = parseInt('9999' + moment().utc().add(user.timezone || 0, 'hours').format('YYYYMMDDHHmmss'))
      const id = this.selectedPost.key
      const message = this.draft.trim()
      const dispatch = this.replyTarget
        ? this.$store.dispatch(`${NS}/replyComment`, { townhallId: id, comment: this.replyTarget, message, likeorder, notifications: [] })
        : this.$store.dispatch(`${NS}/comment`, { townhallId: id, message, likeorder, notifications: [] })
      // Wrap so `.then` is safe even if a mock dispatch returns undefined.
      // Draft is cleared only on success, so a failed write keeps the text
      // for retry; surface the failure instead of letting it vanish silently.
      Promise.resolve(dispatch).then(() => {
        this.draft = ''
        this.replyTarget = null
        this.$store.dispatch(`${NS}/getComment`, { id })
      }).catch(() => {
        if (this.$message) this.$message.error('Could not post your comment. Please try again.')
      })
    },
  },
}
</script>

<style scoped>
.thi { font-family: 'Lato', Arial, sans-serif; color: #4a4a4a; }
.thi-topbar { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; margin-bottom: 16px; }
.thi-title { display: flex; align-items: baseline; gap: 8px; }
.thi-n { font-size: 24px; font-weight: 900; color: #1a1a1a; }
.thi-lbl { font-size: 15px; font-weight: 600; color: #626262; }
.thi-topbar-right { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
.thi-sections { display: flex; gap: 6px; flex-wrap: wrap; }
.thi-sec { display: inline-flex; align-items: center; gap: 6px; padding: 5px 12px; border-radius: 999px; font-size: 12.5px; font-weight: 700; color: #626262; cursor: pointer; }
.thi-sec.on { background: #f2ecfb; color: #6c30cc; }
.thi-cnt { font-style: normal; font-size: 11px; font-weight: 800; opacity: .7; }
.thi-composewrap { position: relative; }
.thi-compose { display: inline-flex; align-items: center; gap: 6px; height: 34px; padding: 0 14px; border: none; border-radius: 8px; background: #32c2d5; color: #fff; font-size: 13px; font-weight: 700; cursor: pointer; font-family: 'Lato', Arial, sans-serif; }
.thi-compose:hover { background: #1aa4b6; }
.thi-compose-plus { font-size: 17px; font-weight: 800; line-height: 1; }
.thi-compose .el-icon-arrow-down { font-size: 10px; opacity: .85; }
.thi-composemenu { position: absolute; right: 0; top: 40px; width: 190px; background: #fff; border: 1px solid #e2e4ea; border-radius: 8px; box-shadow: 0 10px 25px rgba(17,24,39,.10); padding: 6px; z-index: 9; }
.thi-composemenu-item { display: flex; align-items: center; gap: 9px; padding: 9px 10px; border-radius: 6px; font-size: 13px; font-weight: 700; color: #4a4a4a; cursor: pointer; }
.thi-composemenu-item:hover { background: #f2ecfb; color: #6c30cc; }
.thi-composemenu-item i { font-size: 15px; }
.thi-composemenu-divider { height: 1px; margin: 5px 6px; background: #eef0f5; }

.thi-grid { display: grid; grid-template-columns: 330px 1fr; background: #fff; border: 1px solid #eef0f5; border-radius: 16px; overflow: hidden; height: clamp(520px, 72vh, 780px); box-shadow: 0 12px 32px rgba(17, 24, 39, .08); }
.thi-list-col { border-right: 1px solid #eef0f5; display: flex; flex-direction: column; min-width: 0; min-height: 0; position: relative; }
.thi-toolbar { display: flex; align-items: center; gap: 8px; padding: 16px 16px 10px; }
.thi-search { flex: 1; height: 34px; padding: 0 12px; border: 1px solid #e2e4ea; border-radius: 8px; font-size: 12.5px; outline: none; }
.thi-tagwrap { position: relative; }
.thi-tagbtn { display: inline-flex; align-items: center; gap: 5px; height: 34px; padding: 0 11px; border: 1px solid #e2e4ea; border-radius: 8px; font-size: 13px; font-weight: 800; color: #626262; cursor: pointer; }
.thi-tagbtn.on { background: rgba(108,48,204,.08); border-color: rgba(108,48,204,.25); color: #6c30cc; }
.thi-tagbtn .el-icon-arrow-down { font-size: 10px; opacity: .6; }
.thi-tagmenu { position: absolute; right: 0; top: 40px; width: 220px; background: #fff; border: 1px solid #e2e4ea; border-radius: 8px; box-shadow: 0 10px 25px rgba(17,24,39,.08); padding: 6px; z-index: 8; }
.thi-tagmenu-head { font-size: 10.5px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: #8a8a8a; padding: 6px 10px 8px; }
.thi-tagmenu-empty { font-size: 12px; color: #8a8a8a; padding: 4px 10px 8px; }
.thi-tagmenu-item { display: flex; align-items: center; justify-content: space-between; padding: 8px 10px; border-radius: 6px; font-size: 13px; font-weight: 700; cursor: pointer; }
.thi-tagmenu-item:hover { background: #fbf9ff; }
.thi-tagmenu-item.sel { background: #f2ecfb; }
.thi-tagmenu-item .t { color: #6c30cc; }
.thi-tagmenu-item .c { font-size: 11px; font-weight: 800; color: #8a8a8a; }
.thi-activefilter { padding: 0 16px 8px; }
.thi-chip { display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; border-radius: 999px; background: #f2ecfb; color: #6c30cc; font-size: 12px; font-weight: 700; }
.thi-chip .el-icon-close { cursor: pointer; }
.thi-chip.clickable { cursor: pointer; }

.thi-scroll { flex: 1; min-height: 0; overflow-y: auto; }
.thi-empty { padding: 28px 16px; color: #8a8a8a; font-size: 13px; text-align: center; }
.thi-more { padding: 12px 16px; text-align: center; font-size: 12px; font-weight: 700; color: #6c30cc; cursor: pointer; border-top: 1px solid #eef0f5; }
.thi-more:hover { background: #fbf9ff; }
.thi-grouphead { position: sticky; top: 0; z-index: 2; background: #fff; font-size: 10.5px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: #8a8a8a; padding: 16px 16px 8px; border-bottom: 1px solid #f2f3f7; }
.thi-msg { display: flex; gap: 13px; padding: 16px; border-left: 2.5px solid transparent; border-bottom: 1px solid #eef0f5; cursor: pointer; }
.thi-msg:hover { background: #fafbfc; }
.thi-msg.sel { background: #fbf9ff; border-left-color: #6c30cc; }
.thi-av { width: 32px; height: 32px; border-radius: 50%; background: #f2ecfb; color: #6c30cc; font-size: 11px; font-weight: 800; display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto; }
.thi-av.sm { width: 24px; height: 24px; font-size: 9px; }
.thi-type { width: 32px; height: 32px; border-radius: 9px; display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto; }
.thi-type i { font-size: 15px; }
.thi-msg-col { min-width: 0; flex: 1; }
.thi-msg-l1 { display: flex; align-items: center; gap: 6px; }
.thi-mega { color: #6c30cc; font-size: 13px; }
.thi-subj { font-size: 13.5px; line-height: 1.35; font-weight: 700; color: #1a1a1a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.thi-prev { font-size: 12px; color: #626262; margin-top: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.thi-msg-l3 { display: flex; align-items: center; gap: 8px; margin-top: 9px; }
.thi-creator { max-width: 96px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 11px; font-weight: 700; color: #626262; }
.thi-dot { color: #8a8a8a; flex: 0 0 auto; }
.thi-aud { display: inline-flex; padding: 1px 7px; border-radius: 999px; border: 1px solid #e2e4ea; background: #fafbfc; font-size: 10.5px; font-weight: 600; color: #626262; white-space: nowrap; }
.thi-read { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 700; color: #626262; }
.thi-mini { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 700; }
.thi-mini.sched { color: #8a6203; }
.thi-mini.draft { color: #8a8a8a; }
.thi-time { font-size: 11px; color: #8a8a8a; margin-left: auto; flex: 0 0 auto; }

.thi-reader { display: flex; flex-direction: column; min-width: 0; min-height: 0; overflow: hidden; }
.thi-reader-empty { align-items: center; justify-content: center; color: #8a8a8a; font-size: 13px; }
.thi-rd-head { padding: 22px 24px 18px; border-bottom: 1px solid #eef0f5; }
.thi-rd-eyebrow { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.thi-badge { display: inline-flex; align-items: center; gap: 5px; padding: 3px 9px; border-radius: 999px; background: #6c30cc; color: #fff; font-size: 10px; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; }
.thi-rd-date { font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: #8a8a8a; }
.thi-rd-actions { margin-left: auto; display: inline-flex; align-items: center; gap: 8px; }
.thi-pin-toggle { display: inline-flex; align-items: center; gap: 8px; padding: 5px 8px 5px 12px; border-radius: 999px; border: 1px solid #e2e4ea; background: #fff; color: #626262; font-size: 12.5px; font-weight: 700; cursor: pointer; transition: background .15s, color .15s, border-color .15s; }
.thi-pin-toggle:hover { border-color: #c8b1ee; color: #6c30cc; }
.thi-pin-toggle.on { background: rgba(108, 48, 204, .08); color: #6c30cc; border-color: rgba(108, 48, 204, .25); }
.thi-pin-toggle.on i { color: #6c30cc; }
.thi-pin-toggle:disabled { cursor: default; }
.thi-pin-toggle i { font-size: 14px; }
.thi-pin-switch { position: relative; width: 30px; height: 18px; border-radius: 999px; background: #d4d7df; transition: background .2s ease; flex: 0 0 auto; }
.thi-pin-toggle.on .thi-pin-switch { background: #6c30cc; }
.thi-pin-knob { position: absolute; top: 2px; left: 2px; width: 14px; height: 14px; border-radius: 999px; background: #fff; box-shadow: 0 1px 2px rgba(0, 0, 0, .2); transition: transform .2s ease; }
.thi-pin-toggle.on .thi-pin-knob { transform: translateX(12px); }
.thi-republish { display: inline-flex; align-items: center; gap: 6px; height: 30px; padding: 0 14px; border: none; border-radius: 999px; background: #6c30cc; color: #fff; font-size: 12.5px; font-weight: 700; cursor: pointer; font-family: 'Lato', Arial, sans-serif; }
.thi-republish:hover { background: #522698; }
.thi-republish i { font-size: 14px; }
.thi-actions-wrap { position: relative; }
.thi-actions-trigger { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; border: 1px solid #e2e4ea; border-radius: 8px; background: #fff; color: #626262; cursor: pointer; }
.thi-actions-trigger:hover { border-color: #c8b1ee; color: #6c30cc; }
.thi-actions-trigger i { font-size: 16px; }
.thi-actions-menu { position: absolute; right: 0; top: 38px; width: 180px; background: #fff; border: 1px solid #e2e4ea; border-radius: 8px; box-shadow: 0 10px 25px rgba(17,24,39,.10); padding: 6px; z-index: 9; }
.thi-actions-item { display: flex; align-items: center; gap: 9px; padding: 9px 10px; border-radius: 6px; font-size: 13px; font-weight: 700; color: #4a4a4a; cursor: pointer; }
.thi-actions-item:hover { background: #f2ecfb; color: #6c30cc; }
.thi-actions-item i { font-size: 15px; }
.thi-actions-item.danger { color: #cc3030; }
.thi-actions-item.danger:hover { background: rgba(204,48,48,.08); color: #cc3030; }
.thi-draft-banner { background: rgba(108,48,204,.06); }
.thi-draft-banner .thi-sched-when { color: #6c30cc; }
.thi-rd-title { font-size: 19px; font-weight: 800; color: #1a1a1a; margin-top: 11px; line-height: 1.3; word-break: break-word; }
.thi-rd-meta { display: flex; align-items: center; gap: 8px; margin-top: 12px; font-size: 12px; color: #8a8a8a; }
.thi-rd-meta b { color: #4a4a4a; font-weight: 700; }
.thi-rd-tags { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 12px; }
.thi-reach { display: flex; align-items: center; gap: 10px; padding: 14px 24px; background: #fafbfd; border-bottom: 1px solid #eef0f5; }
.thi-reach-pill { display: inline-flex; align-items: center; gap: 7px; padding: 6px 12px; border-radius: 999px; background: rgba(58,196,125,.10); color: #1a7a4d; font-size: 13px; font-weight: 700; }
.thi-reach-meta { font-size: 12.5px; color: #626262; font-weight: 600; }
.thi-sched-banner { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 12px 24px; background: rgba(247, 185, 36, .10); border-bottom: 1px solid #eef0f5; }
.thi-sched-when { display: inline-flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 700; color: #8a6203; }
.thi-sched-when i { font-size: 15px; }
.thi-sched-cancel { border: none; background: transparent; padding: 4px 8px; font-size: 12.5px; font-weight: 700; color: #cc3030; cursor: pointer; font-family: 'Lato', Arial, sans-serif; border-radius: 6px; }
.thi-sched-cancel:hover { background: rgba(204, 48, 48, .08); }
.thi-rd-scroll { flex: 1; min-height: 0; overflow-y: auto; }
.thi-rd-body { padding: 22px 24px; font-size: 14px; line-height: 1.7; color: #4a4a4a; }
.thi-body-text { white-space: pre-wrap; word-break: break-word; }

.thi-poll { padding: 8px 24px 22px; }
.thi-poll-total { font-size: 11px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: #8a8a8a; margin-bottom: 14px; }
.thi-poll-row { margin-bottom: 16px; }
.thi-poll-row:last-child { margin-bottom: 0; }
.thi-poll-label { font-family: 'Lato', Arial, sans-serif; font-size: 14px; font-weight: 500; color: #4a4a4a; margin-bottom: 7px; word-break: break-word; }
.thi-poll-bar { display: flex; align-items: center; gap: 10px; }
.thi-poll-track { flex: 1; height: 16px; background: rgba(26, 26, 46, .06); border-radius: 10px; overflow: hidden; }
.thi-poll-fill { height: 100%; border-radius: 10px; min-width: 2px; }
.thi-poll-percent { min-width: 38px; text-align: right; font-size: 13px; font-weight: 700; color: #8a8a8a; font-variant-numeric: tabular-nums; }

.thi-checklist { padding: 8px 24px 22px; }
.thi-checklist-item { display: flex; align-items: center; gap: 10px; padding: 10px 0; border-bottom: 1px solid #f2f3f7; }
.thi-checklist-item:last-child { border-bottom: none; }
.thi-checklist-mark { font-size: 18px; color: #8a8a8a; flex: 0 0 auto; }
.thi-checklist-label { font-size: 14px; color: #4a4a4a; word-break: break-word; }

.thi-comments { padding: 4px 24px 24px; border-top: 1px solid #eef0f5; }
.thi-cmt-head { font-size: 10.5px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: #8a8a8a; padding: 18px 0 12px; }
.thi-cmt-empty { font-size: 13px; color: #8a8a8a; padding: 4px 0 8px; }
.thi-cmt { display: flex; gap: 11px; padding: 12px 0; border-top: 1px solid #f2f3f7; }
.thi-cmt:first-of-type { border-top: none; }
.thi-cmt-col { min-width: 0; flex: 1; }
.thi-cmt-meta { display: flex; align-items: center; gap: 8px; font-size: 12px; color: #8a8a8a; flex-wrap: wrap; }
.thi-cmt-meta b { color: #1a1a1a; font-weight: 700; font-size: 12.5px; }
.thi-cmt-time { font-weight: 600; }
.thi-cmt-like { display: inline-flex; align-items: center; gap: 3px; color: #f7b924; font-weight: 700; }
.thi-cmt-text { font-size: 13px; line-height: 1.6; color: #4a4a4a; margin-top: 4px; white-space: pre-wrap; word-break: break-word; }
.thi-cmt-sub { display: flex; gap: 8px; margin-top: 12px; padding-left: 12px; border-left: 2px solid #f2f3f7; }
.thi-av.xs { width: 20px; height: 20px; font-size: 8px; }
.thi-cmt-reply { font-size: 11.5px; font-weight: 700; color: #6c30cc; cursor: pointer; }
.thi-cmt-reply:hover { color: #522698; }

.thi-composer { flex: 0 0 auto; border-top: 1px solid #eef0f5; background: #fff; padding: 14px 24px; }
.thi-composer-off { font-size: 12px; color: #8a8a8a; padding: 2px 0; }
.thi-replychip { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; margin-bottom: 8px; border-radius: 999px; background: #f2ecfb; color: #6c30cc; font-size: 12px; font-weight: 600; }
.thi-replychip b { font-weight: 800; }
.thi-replychip .el-icon-close { cursor: pointer; font-weight: 800; }
.thi-composer-row { display: flex; align-items: center; gap: 8px; }
.thi-composer-input { flex: 1; height: 36px; padding: 0 12px; border: 1px solid #e2e4ea; border-radius: 8px; font-size: 13px; outline: none; font-family: 'Lato', Arial, sans-serif; }
.thi-composer-input:focus { border-color: #6c30cc; }
.thi-composer-send { height: 36px; padding: 0 18px; border: none; border-radius: 8px; background: #6c30cc; color: #fff; font-size: 13px; font-weight: 700; cursor: pointer; }
.thi-composer-send:hover { background: #522698; }
.thi-composer-send:disabled { background: #e2e4ea; color: #9a9a9a; cursor: not-allowed; }

@media (max-width: 880px) {
  .thi-grid { grid-template-columns: 1fr; }
  .thi-reader { display: none; }
}
</style>
